import {computed, inject, Injectable} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {ICompanySettings} from '@core/api/data-access';
import {StorageKeys} from '@core/storage';
import {SignalStore} from '@feature-signal-store';
import {ToastService} from '@utils';
import {catchError, filter, first, map, Observable, of} from 'rxjs';
import {useDevtools, useStorePersistence} from 'signalstory';
import {CompanySettingsService} from '../services';
import {companySettingsInitialState, ICompanySettingsState} from './company-settings.state';

@Injectable({
	providedIn: 'root',
})
export class CompanySettingsStore extends SignalStore<ICompanySettingsState> {
	/* ------------ Providers / Services ------------ */
	private companySettingService = inject(CompanySettingsService);
	private toastService = inject(ToastService);
	/* ---------------- Properties ---------------- */
	/* ---------------- Computed ---------------- */
	public readonly companySettings$ = computed<ICompanySettings>(() => this.state().settings);
	public readonly companySettingsObs$ = toObservable(this.companySettings$).pipe(filter(Boolean));

	// Computed properties:
	public readonly availableCommunicationMethods$ = computed<('whatsapp' | 'sms')[]>(() => {
		const settings = this.state().settings;
		if (!settings) return [];
		const methods = [];
		if (Boolean(settings.postalAccount)) methods.push('whatsapp');
		if (Boolean(settings.smsAccountSid)) methods.push('sms');
		return methods;
	});

	/* ---------------- Constructor ---------------- */
	constructor() {
		super('Company Settings', 'Global', companySettingsInitialState, {
			plugins: [
				useStorePersistence({
					persistenceKey: StorageKeys.Stores.COMPANY_SETTINGS_STORE,
					persistenceStorage: localStorage,
				}),
				useDevtools(),
			],
		});
	}

	public override onLogout(state?: ICompanySettingsState, initialState?: ICompanySettingsState): ICompanySettingsState {
		return {...initialState};
	}

	/* -------------------------------- Http Methods -------------------------------- */
	public _loadCompanySettings(force = false): Observable<ICompanySettings> {
		const state = this.state();
		if (this.isCompanySettingsObject(state.settings) && !force) return of(state.settings);
		this.update(state => ({...state, loading: true}));
		return this.companySettingService.get().pipe(
			map(companySettings => {
				this.update(state => ({
					...state,
					settings: companySettings,
					loading: false,
					loaded: true,
				}));
				return companySettings;
			}),
			catchError(error => {
				this.update(state => ({...state, loading: false, error}));
				return of(null);
			}),
			filter(Boolean),
			first(), // Unsubscribe after the first value
		);
	}

	public _updateCompanySettings(companySettings: ICompanySettings): Observable<ICompanySettings> {
		this.update(state => ({...state, settings: companySettings}));
		return this.companySettingService.setCompanySettings(companySettings).pipe(
			catchError(error => {
				this.update(state => ({...state, error}));
				return of(null);
			}),
		);
	}

	/* -------------------------------- State Methods -------------------------------- */
	public isCompanySettingsObject(value: object) {
		return value && Object.keys(value).length > 0;
	}

	public loadCompanySettings(force = false): void {
		this._loadCompanySettings(force).subscribe();
	}

	public updateCompanySettings(companySettings: ICompanySettings): void {
		this._updateCompanySettings(companySettings).subscribe(res => {
			if (res) {
				this.toastService.open({message: 'pages.settings.toast.success', type: 'success', position: 'top-center'});
			} else {
				this.toastService.open({message: 'pages.settings.toast.failed', type: 'error'});
			}
		});
	}

	/* -------------------------------- Private Methods -------------------------------- */
}
